<template lang="pug">
    page(class="view" :delegated-loading="true" :loading="$wait.waiting([keys.riskAssessments.read, keys.riskAssessments.check_cancel, keys.riskAssessments.update, keys.objects.read, keys.objectTypes.read])")
        template(#title="")
            | {{$t('pages.dashboard.service.risk-assessment.view.title')}}
        template(#content="")
            m-block-page-assessment-object-info(v-if="assessmentObject" :item="assessmentObject" showLegend)
            m-accordion-risk-assessment(v-if="configuration && answers.length" :configuration="configuration" :answers="answers" :isOnboarding="isOnboarding" class="mt-4")
            v-layout(wrap justify-space-between class="view-box-btn my-4")
                v-flex(order-md3 md6 lg5 :order-sm3="!$userRoles.isCommonAdmin" align-self-end  md4 :sm12="$userRoles.isCommonAdmin")
                    v-layout(v-if="$userRoles.isCommonAdmin" justify-end)
                        v-flex(xl8 md12 :md4="$userRoles.isCommonAdmin" :sm12="$userRoles.isCommonAdmin" class="relative")
                            e-input-element-status(:loading="loading" @changeButtonVal="selectHandler" :isOnboarding="isOnboarding")
                            div(class="clickArea" @click="ButtonHandler")
                        div(class="tooltipClasses")
                            m-tooltip-info
                                div(v-html="$t('pages.dashboard.service.risk-assessment.view.tooltips.buttonOverflow')")
                    v-layout(v-else justify-end)
                        v-flex(class="relative")
                            e-button-primary(@click="saveAssessment")
                                | {{$t(`pages.dashboard.service.risk-assessment.view.buttonsText.save`)}}
                v-flex( order-md1  md4)
                    v-layout(align-center)
                        v-flex
                            e-button-secondary(
                                @click="cancelAssessment(null)"
                                class="ma-0 mt-2" )
                                | {{$t('pages.dashboard.service.risk-assessment.view.buttonsText.cancel')}}
            v-layout(wrap justify-end class="mt-4")
                v-flex(shrink xs12 md6 class="view__buttons")
                    e-button-primary(@click="printTemplate" :loading="$wait.waiting(keys.effectiveness.pdfDownload)")
                        | {{$t('modules.blocks.content.m-block-content-effectiveness-assessment.buttons.print')}}

</template>

<script>
    import Page from "@pages/templates/page";

    import {mapActions, mapGetters} from 'vuex';
    import links from "@mixins/helpers/_links";
    import dialogs from "@mixins/includes/_dialogs";
    import vuexKeys from "@mixins/includes/_vuexKeys";
    import breadcrumbs from "@mixins/helpers/_breadcrumbs";
    import FileDownload from 'js-file-download';
    import notifications from "@mixins/includes/_notifications";
    import errorHandler from "@mixins/responses/_errorHandler";
    import MAccordionRiskAssessment from "@modules/accordions/concrete/risk-assessment/m-accordion-risk-assessment";
    import MBlockPageAssessmentObjectInfo from "@modules/blocks/page/concrete/m-block-page-assessment-object-info";
    import {generateEmptyAnswersFromConfig} from "@utils/assessmentAnswers";
    import MTooltipInfo from "@modules/tooltips/concrete/m-tooltip-info";

    export default {
        name: "dashboard-service-risk-assessment-view",
        mixins: [links, dialogs, vuexKeys, notifications, errorHandler, breadcrumbs],
        components: {MTooltipInfo, MBlockPageAssessmentObjectInfo, MAccordionRiskAssessment, Page},
        metaInfo() {
            return {
                title: this.$t('pages.dashboard.service.risk-assessment.view.meta.title')
            }
        },
        data: () => ({
            needConfirmRedirect: true,
            loading: false
        }),
        props: {
            isOnboarding: {
                type: Boolean,
                default: false
            }
        },
        async created() {
            try {
                await this.requestAssessment();
                if (this.$lodash.isEmpty(this.answers)) {
                    this.updateAnswers();
                }
                this.$iBreadcrumbs.setText(this.assessment.metadata.name);
                await this.requestObject();
                await this.requestObjectType();
            } catch (e) {
                if (e.response && e.response.status === 404) {
                    this.needConfirmRedirect = false
                    // TODO define server side error
                    this.notify.error(this.$t('notifications.errors.loseRightsOnRa'))
                    this.redirect(this.nav.dashboard.service.risk_assessment.index.name);
                } else if (e.response && e.response.status === 403) {
                    this.needConfirmRedirect = false
                    this.error.handle(e);
                } else {
                    this.error.handle(e);
                }
            }
            this.currentAssessmentId = this.$route.params.id;
            this.$store.dispatch('buttonState/setButtonState', this.$t(`pages.dashboard.service.risk-assessment.view.buttonsText.save`))
        },
        async beforeRouteLeave(to, from, next) {
            if (to.name === this.nav.dashboard.service.risk_assessment.process.name || !this.needConfirmRedirect || this.$lodash.isEmpty(this.$user)) {
                return next();
            }
            await this.cancelAssessment(next)
        },
        computed: {
            ...mapGetters({
                getAssessment: 'riskAssessments/read',
                getCancelAbility: 'riskAssessments/canCancelCurrentRa',
                getObject: 'objects/read',
                getObjectType: 'objectTypes/read'
            }),
            assessment() {
                return this.getAssessment(this.$route.params.id);
            },
            assessmentObject() {
                return this.objectId ? this.getObject(this.objectId) : null
            },
            objectId() {
                if (this.$lodash.isEmpty(this.assessment)) return null;
                return this.$getId(this.assessment._jv.relationships.objects.data.id)
            },
            objectTypeId() {
                if (this.$lodash.isEmpty(this.assessmentObject)) return null;
                return this.$getId(this.assessmentObject._jv.relationships.objectType.data.id)
            },
            configuration() {
                return this.assessment.config
            },
            answers() {
                return this.assessment.configWithAnswer
            },
            canCancelCurrentRa() {
                return this.getCancelAbility();
            },
            buttonState() {
                return this.$store.state.buttonState.buttonState
            }
        },
        methods: {
            ...mapActions({
                loadAssessment: 'riskAssessments/read',
                requestDeleteAssessment: 'riskAssessments/delete',
                updateAssessment: 'riskAssessments/update',
                loadObject: 'objects/read',
                requestUpdateObject: 'objects/update',
                loadObjectType: 'objectTypes/read',
                finishRiskAssessment: 'riskAssessments/finish',
                requestCancelAssessment: 'riskAssessments/cancel',
                requestSaveAssessment: 'riskAssessments/save_draft',
                requestCancelAbility: 'riskAssessments/check_cancel_ability',
                pdfDownloadRequest: 'effectiveness/pdfDownload',
            }),
            async requestAssessment() {
                await this.loadAssessment({id: this.$route.params.id})
            },
            async requestObject() {
                await this.loadObject({id: this.objectId})
            },
            async requestObjectType() {
                await this.loadObjectType({id: this.objectTypeId})
            },
            async cancelAssessment(nextFn) {
                let confirmed = await this.openDialog('cancelAssessment')
                if (!confirmed) return;
                this.needConfirmRedirect = false
                nextFn ? nextFn() : this.redirect(this.nav.dashboard.service.risk_assessment.index.name)
            },
            async saveAssessment() {
                // let confirmed = await this.openDialog('saveAssessment');
                //  if (!confirmed) return;
                this.loading = true;
                this.needConfirmRedirect = false
                try {
                    await this.saveDraft();
                    this.loading = false;
                    this.redirect(this.nav.dashboard.service.risk_assessment.index.name);
                } catch (e) {
                    if (e.response && e.response.status === 404) {
                        // TODO define server side error
                        this.notify.error(this.$t('notifications.errors.loseRightsOnRa'))
                        this.loading = false;
                        this.redirect(this.nav.dashboard.service.risk_assessment.index.name);
                    } else {
                        this.error.handle(e)
                    }
                }
            },

            async finishAssessment() {
                let confirmed = await this.openDialog('submitAssessment');
                if (!confirmed) return;
                this.loading = true;
                this.needConfirmRedirect = false
                try {
                    await this.closeRiskAssessment();
                    this.notify.success(this.$t('notifications.success.risk-assessment-sumitted'));
                    this.loading = false;
                    this.dialogPdf()
                    this.redirect(this.nav.dashboard.service.effectiveness_control.check.name, {
                        objectId: this.objectId
                    })

                } catch (e) {
                    if (e.response && e.response.status === 404) {
                        // TODO define server side error
                        this.notify.error(this.$t('notifications.errors.loseRightsOnRa'))
                        this.redirect(this.nav.dashboard.service.risk_assessment.index.name);
                        this.loading = false;
                    } else {
                        this.error.handle(e)
                    }
                }
            },
            async closeRiskAssessment() {
                const payload = {
                    _id: this.assessment._id
                };

                return await this.finishRiskAssessment(payload)
            },
            async deleteRiskAssessment() {
                return await this.requestCancelAssessment(this.assessment._id);
            },
            async updateAnswers() {
                const answers = generateEmptyAnswersFromConfig(this.configuration);
                const payload = {
                    _id: this.assessment._id,
                    configWithAnswer: answers
                };
                return await this.updateAssessment(payload)
            },
            async saveDraft() {
                const payload = {
                    _id: this.assessment._id
                };
                return await this.requestSaveAssessment(payload);
            },
            async checkCancelAbility() {
                const payload = {
                    _id: this.$route.params.id
                };
                return await this.requestCancelAbility(payload);
            },
            ButtonHandler() {
                if (this.buttonState == this.$t(`pages.dashboard.service.risk-assessment.view.buttonsText.submit`)) {
                    this.finishAssessment();
                } else {
                    this.saveAssessment();
                }
            },
            selectHandler(val) {
                if (val == this.$t(`pages.dashboard.service.risk-assessment.view.buttonsText.submit`)) {
                    this.finishAssessment();
                } else {
                    this.saveAssessment();
                }
            },
            async dialogPdf() {
                this.$store.dispatch('effectiveness/setRedirectId', this.assessment._id)
                await this.openDialog('effectivenessPdf');
            },
            async pdfDownload() {
              try {
                let {pdf, filename} = await this.pdfDownloadRequest({id: this.currentAssessmentId})
                FileDownload(pdf, filename || `effectiveness-assessment${this.currentAssessmentId}.pdf`)
              } catch (e) {
                // log
              }
            },
            printTemplate() {
              const {href} = this.$router.resolve(this.nav.dashboard.print.risk_assessment.parent() + '/' + this.currentAssessmentId)
              window.open(href, '_blank')
            },
        }
    }
</script>

<style lang="scss">
    .view {
        .relative {
            position: relative;
        }

        .view-box-btn {
            .v-select {
                &__selections {
                    flex-wrap: nowrap;
                }
            }
        }

        .clickArea {
            position: absolute;
            top: 1rem;
            left: .7rem;
            z-index: 1;
            right: 3.75rem;
            height: 3rem;
            cursor: pointer;
        }

        .tooltipClasses {
            margin-top: 1rem;

            @include lessMedia(sm) {
                margin-top: .5rem;
            }
        }
      &__buttons {
        display: flex;
        justify-content: flex-end;
        margin-right: 2.7rem;

        @include media(md) {
          padding-left: 1.5rem !important;
        }
      }

    }

</style>
